import React, { useMemo, useCallback, useEffect } from 'react';
import cn from 'clsx';
import { useStateReducer, showNotification, captureException } from 'r/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { AnalysisData } from '../types';

interface VerificationModalProps {
  isOpen: boolean;
  selectedAnalyses: AnalysisData[];
  onClose: () => void;
  onConfirm: (analysisIds: string[]) => Promise<void>;
}

export default function ModalBulkVerification({
  isOpen,
  selectedAnalyses,
  onClose,
  onConfirm,
}: VerificationModalProps) {
  const [{ selectedIds, isSubmitting }, ss] = useStateReducer({
    selectedIds: selectedAnalyses.map((a) => a.id),
    isSubmitting: false,
  });

  useEffect(() => {
    if (isOpen) {
      ss({ selectedIds: selectedAnalyses.map((a) => a.id) });
    }
  }, [isOpen, selectedAnalyses, ss]);

  const handleToggle = useCallback(
    (id: string) => {
      ss((prev: { selectedIds: string[]; isSubmitting: boolean }) => ({
        selectedIds:
          prev.selectedIds.includes(id) ?
            prev.selectedIds.filter((item) => item !== id)
          : [...prev.selectedIds, id],
      }));
    },
    [ss],
  );

  const handleSubmit = useCallback(async () => {
    if (selectedIds.length === 0) return;
    ss({ isSubmitting: true });
    try {
      await onConfirm(selectedIds);
    } catch (error) {
      captureException(error);
      showNotification('error', 'Failed to verify analyses');
    } finally {
      ss({ isSubmitting: false });
    }
  }, [selectedIds, onConfirm, ss]);

  const getVerificationStatus = (analysis: AnalysisData) =>
    analysis.verifiedBy ?
      `Verified by: ${analysis.verifiedBy}`
    : 'Not verified';

  const toBeOverwrittenAnalyses = useMemo(
    () =>
      selectedAnalyses.filter(
        (a) => selectedIds.includes(a.id) && a.verifiedBy,
      ),
    [selectedIds, selectedAnalyses],
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirm Verification</DialogTitle>
        </DialogHeader>

        <div className="max-h-[50vh] overflow-y-auto space-y-3">
          {selectedAnalyses.map((analysis) => {
            const isSelected = selectedIds.includes(analysis.id);
            const isAlreadyVerified = !!analysis.verifiedBy;
            return (
              <div
                key={analysis.id}
                className={cn(
                  'flex items-start gap-3 p-3 border rounded-lg transition-colors',
                  isAlreadyVerified ?
                    'border-yellow-500 bg-yellow-500/10'
                  : 'border-(--textTertiary)',
                  isSelected && 'bg-card',
                )}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleToggle(analysis.id)}
                  className={cn(isAlreadyVerified && 'border-yellow-500')}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold">
                      {analysis.tweetData.name}
                    </span>
                    {isAlreadyVerified && (
                      <span className="bg-yellow-500 text-main text-xs px-2 py-0.5 rounded">
                        Verified
                      </span>
                    )}
                  </div>
                  <p className="text-sm line-clamp-2">
                    {analysis.tweetData.text}
                  </p>
                  <p className="text-xs text-secondary mt-1">
                    {getVerificationStatus(analysis)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {toBeOverwrittenAnalyses.length > 0 && (
          <div className="p-3 bg-yellow-500/10 border border-yellow-500 rounded-lg">
            <p className="text-yellow-600 text-sm">
              ⚠️ {toBeOverwrittenAnalyses.length} analyses are already verified.
              This will overwrite them.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedIds.length === 0 || isSubmitting}
          >
            {isSubmitting ? 'Verifying...' : `Confirm (${selectedIds.length})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
